import { useEffect, useState } from 'react'
import {
  clearPendingRecordingPolicyGate,
  usePendingRecordingPolicyGate,
} from '../../../stores/recording-policy-gate-store'
import {
  hasAcceptedCurrentRecordingPolicy,
  useRecordingPolicyConsent,
} from '../../../stores/recording-policy-store'
import {
  acceptRecordingPolicy,
  declineRecordingPolicy,
  ensureRecordingPolicyConsentLoaded,
} from '../../../services/recording-policy-service'
import { RecordingConsentContent } from '../../setup/components/recording-consent-content'

/**
 * Pre-dial recording-policy gate: shown when a Member/Independent tries to
 * place a call without a durable recording-policy acceptance (never decided, or
 * said no). It asks the same Easy Read question as setup. Yes records the
 * answer and continues the very call they were placing — no redial. No (after
 * "Are you sure?") records a durable DECLINED: calls stay off in both
 * directions (also enforced server-side) and they can change it here or in
 * Menu → Call recording.
 */
export function RecordingPolicyGateDialog() {
  const gate = usePendingRecordingPolicyGate()
  const consent = useRecordingPolicyConsent()
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)

  // The gate opens optimistically before the profile may have loaded (e.g.
  // cold start straight into a call button). Resolve the real status here —
  // an already-accepted user proceeds without ever seeing the dialog.
  useEffect(() => {
    if (!gate || consent.loaded) return
    let cancelled = false
    setLoadError(false)
    ensureRecordingPolicyConsentLoaded().catch(() => {
      if (!cancelled) setLoadError(true)
    })
    return () => {
      cancelled = true
    }
  }, [gate, consent.loaded])

  useEffect(() => {
    if (!gate) return
    if (consent.loaded && hasAcceptedCurrentRecordingPolicy(consent)) {
      const proceed = gate.proceed
      clearPendingRecordingPolicyGate()
      proceed()
    }
  }, [gate, consent])

  useEffect(() => {
    setBusy(null)
    setError(null)
  }, [gate])

  useEffect(() => {
    if (!gate) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearPendingRecordingPolicyGate()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [gate])

  if (!gate) return null
  if (consent.loaded && hasAcceptedCurrentRecordingPolicy(consent)) return null

  const declined = consent.loaded && consent.status === 'DECLINED'
  const loading = !consent.loaded && !loadError
  const peerName = gate.peer?.displayName?.trim()

  const handleAccept = async () => {
    if (busy) return
    setBusy('accept')
    setError(null)
    try {
      await acceptRecordingPolicy('call')
      const proceed = gate.proceed
      clearPendingRecordingPolicyGate()
      // Continue the exact call the user was placing — no redial.
      proceed()
    } catch {
      setBusy(null)
      setError('Couldn’t save your answer. Check your connection and try again.')
    }
  }

  const handleDecline = async (): Promise<boolean> => {
    if (busy) return false
    setBusy('decline')
    setError(null)
    try {
      await declineRecordingPolicy('call')
      setBusy(null)
      return true
    } catch {
      setBusy(null)
      setError('Couldn’t save your choice. Check your connection and try again.')
      return false
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Calls are recorded"
    >
      <div className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl sm:p-6">
        {loading ? (
          <div className="space-y-3">
            <div className="space-y-3" aria-busy="true" aria-label="Checking your answer">
              <div className="h-12 w-12 animate-pulse rounded-2xl bg-slate-100" />
              <div className="h-5 w-2/3 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100" />
            </div>
            <button
              type="button"
              onClick={() => clearPendingRecordingPolicyGate()}
              className="min-h-11 w-full rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 outline-none transition hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              Go back
            </button>
          </div>
        ) : (
          <RecordingConsentContent
            variant="consent"
            lead={peerName ? <>Before your call to <span className="font-medium text-slate-800">{peerName}</span></> : undefined}
            status={
              declined || loadError ? (
              <>
                {declined && (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-[15px] text-amber-900" role="status">
                    You said no to call recording, so calls are off for you. To make this call, say yes.
                  </p>
                )}
                {loadError && (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-[15px] text-amber-900" role="alert">
                    We couldn’t check your answer. You can still say yes below to make calls.
                  </p>
                )}
              </>
              ) : undefined
            }
            busy={busy}
            error={error}
            onAccept={() => void handleAccept()}
            onDecline={declined ? undefined : () => handleDecline()}
            onLater={() => clearPendingRecordingPolicyGate()}
            laterLabel={declined ? 'Close' : 'Go back'}
          />
        )}
      </div>
    </div>
  )
}
